<template>
    <div>
        <!-- Table -->
        <div v-if="invoices.length > 0 || contractSanctions.length > 0" class="card" style="border-radius: 12px; border: 1px solid #e0e0e0;">
            <div class="d-flex align-items-center p-4 mb-2">
                <i class="fas fa-file-invoice me-2"></i>
                <h6 class="mb-0 me-1">{{ $t('invoice_history.invoices') }}</h6>
                <span class="text-muted">({{ invoices.length + contractSanctions.length }})</span>
                <button
                    v-if="isObserver"
                    class="btn btn-outline-success form-button ms-auto"
                    style="min-height: 44px; min-width: 44px;"
                    :disabled="downloadLoading"
                    :title="$t('invoice_history.download_report_tooltip')"
                    @click.stop="$emit('downloadReport')"
                >
                    {{ $t('invoice_history.download_report') }}
                </button>
            </div>
            <div class="table-responsive px-4 pb-4">
                <vue3-datatable
                    v-if="tableRows && tableRows.length > 0"
                    :rows="tableRows"
                    :columns="fields"
                    :pagination="false"
                    :sticky-header="true"
                    :sortable="false"
                    :height="tableRows.length < 7 ? '100%' : '40vh'"
                    skin="bh-table-compact"
                    cell-class="routa-table-cell"
                    :row-class="getRowClass"
                    @rowClick="onRowClick"
                >

                    <template #id="data">
                        <span :class="{ 'credit-note-text': data.value.is_credit_note }">
                            {{ data.value.is_credit_note ? '↳ ' + data.value.id : data.value.id }}
                        </span>
                    </template>

                    <template #contract_name="data"  :class="{ 'credit-note-text': data.value.is_credit_note }">
                        <span :class="{ 'credit-note-text': data.value.is_credit_note }">
                            {{ getContractText(data.value)}}
                        </span>
                    </template>

                    <template #type="data">
                        <span class="type-badge"  :class="{ 'credit-note-text': data.value.is_credit_note }">
                            {{ data.value.is_credit_note ? $t('invoice_history.credit_note')
                                : data.value.is_contract_sanction ? $t('contract_sanction.type_label')
                                : $t('invoice_history.invoice') }}
                        </span>
                    </template>

                    <template #status="data">
                        <span :class="statusClass(data.value.status)">
                            <i v-if="data.value.status === 2" class="fas fa-clock me-1"></i>
                            <i v-if="data.value.status === 3" class="fas fa-check-square me-1"></i>
                            <i v-if="data.value.status === 4" class="fas fa-times me-1"></i>
                            {{ getStatusText(data.value.status) }}
                        </span>
                    </template>
                    <template #sum="data">
                        <div class="text-right" :class="{ 'credit-note-text': data.value.is_credit_note }">{{ formatCurrency(data.value.total_sum / 100) }}</div>
                    </template>
                    <template #number="data">
                        <span :class="{ 'credit-note-text': data.value.is_credit_note }">{{ data.value.number || '-' }}</span>
                    </template>
                    <template #invoice_date="data">
                        <span :class="{ 'credit-note-text': data.value.is_credit_note }">
                            {{ formatDate(data.value.created) }}
                        </span>
                    </template>
                    <template #contractor_number="data">
                        <span>{{ data.value.contractor_number || '-' }}</span>
                    </template>
                    <template #gross_amount="data">
                        <div class="text-right">{{ formatGrossAmount(data.value.gross_amount) }}</div>
                    </template>
                    <template #document_date="data">
                        <span>{{ formatDate(data.value.document_date) }}</span>
                    </template>
                    <template #actions="data">
                        <div v-if="(data.value.status === 3 || data.value.status === 2) && !data.value.is_credit_note && !data.value.is_contract_sanction">
                            <span v-if="!hasContractSystem(user)" class="text-danger small me-2">{{ $t('invoice_history.invoicing_integration_required') }}</span>
                            <button class="btn btn-sm btn-outline-primary" style="font-size: .9em; cursor: pointer; border: 1px solid #0d6efd" :disabled="!hasContractSystem(user)" @click.stop="creditNoteClicked(data.value)">
                                {{ $t('invoice_history.create_credit_note') }}
                            </button>
                        </div>
                    </template>
                </vue3-datatable>
            </div>
        </div>

    </div>
</template>

<script>
import { restApi } from '../mixins/RestApiMixin'
import { timeUtils } from '../mixins/TimeUtils'
import Vue3Datatable from '@bhplugin/vue3-datatable'
import {priceHelper} from "../mixins/PricingMixin.js"
import {invoicingHelper} from "../mixins/InvoicingMixin.js";
import {userHelper} from "../mixins/UserMixin";

export default {
    name: 'InvoiceList',
    components: { Vue3Datatable },
    mixins: [restApi, timeUtils, priceHelper, invoicingHelper, userHelper],
    props: {
        invoices: {
            type: Array,
            default: []
        },
        isObserver: { type: Boolean, default: false },
        downloadLoading: { type: Boolean, default: false },
        contractSanctions: { type: Array, default: () => [] },
        user: { type: Object, default: null }
    },
    watch: {
        invoices() {
            this.initTableRows()
        },
        contractSanctions() {
            this.initTableRows()
        }
    },
    data() {
        return {
            tableRows: [],
            fields: [
                { field: 'id', title: this.$t('common.id'), sort: false, headerClass: 'routa-table-header' },
                { field: 'contract_name', title: this.$t('invoice_history.contract'), sort: false, headerClass: 'routa-table-header' },
                { field: 'invoice_date', title: this.$t('invoice_history.date'), sort: false, headerClass: 'routa-table-header' },
                { field: 'timerange', title: this.$t('common.time_range'), sort: false, headerClass: 'routa-table-header' },
                { field: 'type', title: this.$t('invoice_history.type'), sort: false, headerClass: 'routa-table-header' },
                { field: 'status', title: this.$t('invoice_history.status'), sort: false, headerClass: 'routa-table-header' },
                { field: 'sum', title: this.$t('invoice_history.sum'), sort: false, headerClass: 'routa-table-header' },
                { field: 'number', title: this.$t('invoice_history.invoice_number'), sort: false, headerClass: 'routa-table-header' },
                { field: 'contractor_number', title: this.$t('invoice_history.contractor_number'), sort: false, headerClass: 'routa-table-header' },
                { field: 'gross_amount', title: this.$t('invoice_history.gross_amount'), sort: false, headerClass: 'routa-table-header' },
                { field: 'document_date', title: this.$t('invoice_history.document_date'), sort: false, headerClass: 'routa-table-header' },
                { field: 'actions', title: this.$t('invoice_history.actions'), sort: false, headerClass: 'routa-table-header' }
            ]
        }
    },
    mounted() {
        this.startDate = this.timestampToDateInput(this.getMonthStartAsDate().getTime())
        this.endDate = this.timestampToDateInput(new Date().getTime())
    },
    methods: {
        initTableRows() {
            this.tableRows = []
            // Each invoice (with its credit notes nested right after it)
            // and each contract sanction form a "group" tagged with the
            // record's original backend order (_order, set in
            // InvoiceHistory.vue's handleResponse). Sorting the groups by
            // _order before flattening preserves the backend's ordering
            // instead of always pushing sanctions after every invoice.
            const groups = []
            this.invoices.forEach(inv => {
                inv.timerange = this.getWorkTimeRange(inv)
                const group = [inv]
                if (inv.credit_notes && inv.credit_notes.length > 0) {
                    inv.credit_notes.forEach(cn => {
                        const cnSum = (cn.credit_note_rows || []).reduce((acc, r) => acc + (r.sum || 0), 0)
                        group.push({
                            ...cn,
                            total_sum: cnSum,
                            is_credit_note: true,
                            info: cn.info,
                            contract: inv.contract
                        })
                    })
                }
                groups.push({ order: inv._order ?? 0, rows: group })
            })
            this.contractSanctions.forEach(cs => {
                groups.push({
                    order: cs._order ?? 0,
                    // The real GET /invoice payload for a contract sanction
                    // has no `rows` array (confirmed against backend data,
                    // ROUTA-5584) - its sum is already at the top level in
                    // total_sum, same as invoices and credit notes. Reading
                    // cs.rows?.[0]?.total always fell through to 0.
                    rows: [{
                        ...cs,
                        is_contract_sanction: true,
                        total_sum: cs.total_sum ?? 0
                    }]
                })
            })
            groups.sort((a, b) => a.order - b.order)
            const rows = groups.flatMap(g => g.rows)
            this.$nextTick(() => this.tableRows = rows)
        },

        onRowClick(row) {
            if (row.is_credit_note) {
                this.$emit('creditNoteClicked', row)
            } else if (row.is_contract_sanction) {
                this.$emit('contractSanctionClicked', row)
            } else {
                this.$emit('invoiceClicked', row)
            }
        },

        getRowClass(row) {
            return row.is_credit_note ? 'routa-table-row credit-note-row' : 'routa-table-row'
        },

        creditNoteClicked(row) {
            const invoice = this.invoices.find(inv => inv.id === row.id)
            if (invoice) {
                this.$emit('addCreditNote', invoice)
            }
        }

    }
}
</script>

<style scoped>
.badge {
    padding: 0.4em 0.6em;
    font-size: 0.85em;
}

/* Remove grey background from table headers */
:deep(.routa-table-header) {
    background-color: transparent !important;
}

/* Add unified border below header row */
:deep(.bh-table-compact thead tr) {
    border-bottom: 1px solid #dee2e6 !important;
}

/* Remove grey background from table body */
:deep(.bh-table-compact) {
    background-color: white !important;
}

:deep(.bh-table-compact tbody) {
    background-color: white !important;
}

:deep(.bh-table-compact tr) {
    background-color: white !important;
}

:deep(.bh-table-responsive .dataTable-bottom .dataTable-info) {
    display: none !important;
}

.type-badge {
    display: inline-block;
    padding: 0.25em 0.6em;
    font-size: 0.85em;
    border: 1px solid #ccc;
    border-radius: 6px;
}

.credit-note-text {
    color: #003956 !important;
    font-style: italic;
}

:deep(.credit-note-row td:first-child) {
    padding-left: 5rem !important;
}

.cn-badge {
    background-color: #fff3cd;
    border-color: #ffc107;
    color: #856404;
}

</style>
